/**
 * SRT (SubRip).
 *
 * THERE IS NO FORMAL SPEC. The format's behaviour is whatever the implementations that read
 * it accept, and the three that decide it are ffmpeg (`libavformat/srtdec.c` +
 * `libavcodec/subviewerdec`/`srtdec`), VLC (`modules/demux/subtitle.c`) and mpv (which reads
 * through ffmpeg). Every leniency below is one of theirs, and every one is a fixture with a
 * citation in `test/convert/fixtures/MANIFEST.md`:
 *
 *   • **Cue numbers are optional.** ffmpeg's `srt_probe`/`ff_subtitles_read_text_chunk` keys
 *     on the `-->` line; a file whose numbers were stripped still reads. So a leading integer
 *     is consumed only when a timestamp line follows it.
 *   • **`.` is accepted where the format says `,`.** Files converted from WebVTT by careless
 *     tools carry periods, and every player reads them.
 *   • **1–3 fractional digits**, and the hours field may be omitted. `00:01,5` is 1.5 s.
 *   • **A blank line inside a cue is text**, unless what follows it starts a new cue. Splitting
 *     on every blank line turns one cue into two, the second of which has dialogue where its
 *     timestamp should be — so one malformed cue becomes two and takes a good one with it.
 *   • **`<i> <b> <u> <font color>` survive**; `{\an8}` is read as position and `{\i1}` as
 *     italic, because ASS-sourced SRT carries both.
 *
 * The one thing this parser does NOT do is guess. A block whose timestamp line is unreadable
 * is reported at its LINE NUMBER and skipped; if nothing at all parsed, the whole file comes
 * back `not-recognized` naming the first line that failed, which is what B5's error copy
 * interpolates.
 */

import type {
  Alignment,
  Cue,
  ParseOptions,
  ParseResult,
  ParserDefinition,
  Position,
  RawEvent,
  RunContext,
  TextLine,
} from '../../../contracts/cue-model.ts';
import { MS_GRID, makeCue, originFor } from '../../core/model/build.ts';
import { parseTimestamp, timeFormat } from '../../core/time/timestamp.ts';
import { Draft, safeSpan } from '../common/draft.ts';
import { blocksWithRecovery, type SourceLine, scanLines } from '../common/lines.ts';
import { decodeRuns, parseMarkup, SRT_MARKUP } from '../common/markup.ts';

/** `00:00:12,340 --> 00:00:15,000  X1:040 X2:600 Y1:460 Y2:480` */
const ARROW = /-->/;
const TIME_LINE = /^\s*([^\s]+(?:\s+[^\s>-][^\s]*)*?)\s*-->\s*([^\s]+)\s*(.*)$/;
const INT_ONLY = /^\s*(\d{1,9})\s*$/;
/** The non-standard coordinate suffix ffmpeg preserves and VLC ignores. */
const COORDS = /\b([XY][12])\s*:\s*(-?\d+)/g;

const SRT_TIME = timeFormat('srt');

/**
 * True when the line at `at` begins a new cue.
 *
 * A timestamp line always does. **A bare integer also does**, even when what follows it is not a
 * readable timestamp — because after a blank line a line containing nothing but a number is
 * overwhelmingly a cue number, and treating it as dialogue is worse in both directions: the
 * malformed cue is absorbed into the previous cue's text (which then contains the stray number),
 * and nothing reports the line the user has to fix.
 *
 * The cost is a cue whose text is a bare number AND which follows a blank line inside its own
 * cue — genuinely ambiguous in a format with no specification, and resolved the way ffmpeg and
 * VLC resolve it. A number that follows its timestamp line directly is unaffected, which is the
 * shape `srt-numeric-looking-text` pins.
 */
function startsCue(lines: readonly SourceLine[], at: number): boolean {
  const line = lines[at];
  if (line === undefined) return false;
  if (isTimeLine(line.text)) return true;
  return INT_ONLY.test(line.text);
}

function isTimeLine(text: string): boolean {
  if (!ARROW.test(text)) return false;
  const m = TIME_LINE.exec(text);
  if (m === null) return false;
  return parseTimestamp(m[1] as string, SRT_TIME) !== null;
}

export function parseSrt(text: string, opts: ParseOptions, ctx: RunContext): ParseResult {
  const draft = new Draft({ alloc: ctx.alloc, opts, sourceFormat: 'srt', grid: MS_GRID });
  const lines = scanLines(text);
  const blocks = blocksWithRecovery(lines, startsCue);

  let firstFailure: string | null = null;
  let ordinal = 0;

  for (const block of blocks) {
    let at = 0;
    let index: number | null = null;

    const head = block[0] as SourceLine;
    const numberMatch = INT_ONLY.exec(head.text);
    if (
      numberMatch !== null &&
      block[1] !== undefined &&
      isTimeLine((block[1] as SourceLine).text)
    ) {
      index = Number(numberMatch[1]);
      at = 1;
    }

    const timeLine = block[at];
    if (timeLine === undefined || !ARROW.test(timeLine.text)) {
      // Point at the line where a TIMESTAMP was expected, which is the line after the cue
      // number when there is one. Naming the number's line sends the user to a line that is
      // perfectly fine.
      const where = at === 0 && INT_ONLY.test(head.text) ? (block[1] ?? head) : (timeLine ?? head);
      const message = `expected a timestamp like 00:01:23,456 --> 00:01:25,000, found ${quote(where.text)}`;
      draft.diag('malformed-timestamp', message, { sourceLine: where.no });
      firstFailure ??= `line ${where.no}: ${message}`;
      continue;
    }

    const m = TIME_LINE.exec(timeLine.text);
    const start = m === null ? null : parseTimestamp(m[1] as string, SRT_TIME);
    const end = m === null ? null : parseTimestamp(m[2] as string, SRT_TIME);
    if (m === null || start === null || end === null) {
      const message = `expected a timestamp like 00:01:23,456 --> 00:01:25,000, found ${quote(timeLine.text)}`;
      draft.diag('malformed-timestamp', message, { sourceLine: timeLine.no });
      firstFailure ??= `line ${timeLine.no}: ${message}`;
      continue;
    }

    const body = block
      .slice(at + 1)
      .map((l) => l.text)
      .join('\n')
      // Trailing blank lines inside a recovered block are the separator we re-added; they are
      // not content at the END of a cue.
      .replace(/\n+$/, '');

    const id = ctx.alloc.cue();
    const time = safeSpan(start as number, end as number, draft, {
      cueId: id,
      sourceLine: timeLine.no,
    });
    const markup = parseMarkup({
      text: body,
      alloc: ctx.alloc,
      caps: SRT_MARKUP,
      cueStart: time.start,
      cueEnd: time.end,
    });
    const textLines = decodeRuns(markup.lines) as TextLine[];

    const position: Position | null =
      markup.alignment === null
        ? null
        : {
            alignment: markup.alignment as Alignment,
            point: null,
            move: null,
            rotationOrigin: null,
            clip: null,
            sizePct: null,
            vertical: null,
            line: null,
            positionPct: null,
          };

    const extra = coordsOf(m[3] ?? '');
    if (Object.keys(extra).length > 0) {
      draft.diag(
        'ass-format-column-unknown',
        `SRT coordinate suffix ${Object.entries(extra)
          .map(([k, v]) => `${k}:${v}`)
          .join(' ')} preserved verbatim; no player in the reference set renders it`,
        { cueId: id, sourceLine: timeLine.no },
      );
    }

    const raw: RawEvent | null = opts.retainRaw
      ? {
          format: 'srt',
          fields: {
            Index: String(index ?? ordinal + 1),
            Start: (m[1] as string).trim(),
            End: (m[2] as string).trim(),
            Text: body,
          },
          timeFields: ['Start', 'End'],
          stale: false,
        }
      : null;

    const base = makeCue({
      id,
      time,
      lines: textLines,
      styleId: draft.defaultStyleId,
      trackId: draft.defaultTrackId,
      origin: { ...originFor(draft.inputId, 'parse', ordinal), sourceLine: timeLine.no },
      index,
      granularity: 'coalesced',
    });
    const cue: Cue = { ...base, position, extra, raw };
    draft.addCue(cue);
    ordinal++;
  }

  /*
   * ZERO CUES IS NOT THE SAME AS A FAILURE, and USER_JOURNEY B5 spells out both messages:
   *
   *   "This doesn't parse as {SRT}. Parsing failed at line {41}…"   ← a failure was recorded
   *   "This file parsed, but it's empty — no cues found."           ← it parsed; there was nothing
   *
   * So a file with a malformed cue comes back `not-recognized` naming the line, and a file that
   * is structurally fine and simply has no cues comes back OK with zero cues, which is the state
   * the second message renders from. Collapsing the two would make one of the two strings
   * unreachable.
   */
  if (firstFailure !== null && draft.cues.length === 0) {
    return draft.notRecognized(firstFailure);
  }
  return draft.ok();
}

function quote(s: string): string {
  const trimmed = s.trim();
  const shown = trimmed.length > 60 ? `${trimmed.slice(0, 57)}…` : trimmed;
  return `"${shown}"`;
}

function coordsOf(tail: string): Record<string, string> {
  const out: Record<string, string> = {};
  COORDS.lastIndex = 0;
  let m: RegExpExecArray | null = COORDS.exec(tail);
  while (m !== null) {
    out[m[1] as string] = m[2] as string;
    m = COORDS.exec(tail);
  }
  return out;
}

/**
 * Detection.
 *
 * A WebVTT file also contains `-->`, so the signature check comes first and hard-zeroes: an
 * SRT parser winning the sniff on a `.vtt` would silently drop every cue setting in the file.
 */
export function sniffSrt(text: string, filename: string | null): number {
  if (/^﻿?WEBVTT(\s|$)/.test(text)) return 0;
  const numbered = /(^|\n)\s*\d{1,9}\s*\n\s*\d{1,2}:\d{2}:\d{2}[,.]\d{1,3}\s*-->/.test(text);
  const arrow = /\d{1,2}:\d{2}:\d{2}[,.]\d{1,3}\s*-->\s*\d{1,2}:\d{2}:\d{2}/.test(text);
  const ext = (filename ?? '').toLowerCase().endsWith('.srt');
  if (numbered) return ext ? 0.98 : 0.9;
  if (arrow) return ext ? 0.85 : 0.7;
  // A file with `-->` and something timestamp-shaped, but not SRT's shape.
  if (ARROW.test(text) && ext) return 0.4;
  return 0;
}

export const srtParser: ParserDefinition = {
  format: 'srt',
  sniff: sniffSrt,
  requiresFramerate: false,
  parse: parseSrt,
};
